import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Typography,
} from '@mui/material';
import { getExpenseAssociatedInvoices } from '../../Services/ExpenseService';
import { getInvoicePdf } from '../../Services/InvoiceService';

import { COLORS } from '../../../utils/Application_Constants';

const ExpenseCorrectionDialog = ({
    open,
    onClose,
    onConfirm,
    expenseId,
    isLoading,
    isFullCredit,
}) => {
    const [linkedInvoices, setLinkedInvoices] = useState([]);
    const [expenseCreditHistoryLoading, setExpenseCreditHistoryLoading] = useState(true);
    const [expenseCreditHistoryFailed, setExpenseCreditHistoryFailed] = useState();
    const [retry, setRetry] = useState();
    const [showAlert, setShowAlert] = useState();
    const [showLinkedInvoices, setShowLinkedInvoices] = useState();

    const handleRetry = () => {
        setRetry((prev) => prev + 1);
        setExpenseCreditHistoryLoading(true);
    };

    useEffect(async () => {
        /// Fetch linked invoices
        try {
            if (expenseId) {
                const invoices = await getExpenseAssociatedInvoices(expenseId);
                if (invoices && invoices.length) {
                    const invoiceIds = invoices.map((invoice) => ({
                        id: invoice.id,
                        invoiceNumber: invoice.invoiceNumber ?? null,
                    }));
                    setLinkedInvoices(
                        invoiceIds.filter(({ invoiceNumber }) => invoiceNumber !== null)
                    );
                }
            }
        } catch (e) {
            console.error(`Error fetching credit history for expense ${expenseId}: ${e}`);
            setExpenseCreditHistoryFailed(true);
        } finally {
            setExpenseCreditHistoryLoading(false);
        }
    }, [expenseId, retry]);

    const openPdf = async (invoiceId) => {
        try {
            if (!invoiceId) {
                throw new Error('Invalid invoiceId parameter');
            }

            const pdf = await getInvoicePdf(invoiceId);
            const { base64Data } = pdf;

            if (!base64Data) {
                throw new Error(`PDF not received for invoice ${invoiceId}`);
            }
            const newTab = window.open('', '_blank');

            // Convert base64 string to binary Blob
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });

            const pdfUrl = URL.createObjectURL(blob);
            newTab.location.href = pdfUrl;
        } catch (e) {
            console.error(`There was an issue opening the PDF for Invoice ${invoiceId}: ${e}`);
        }
    };

    useEffect(() => {
        if (isFullCredit === true || isFullCredit === false) return setShowAlert(true);

        return setShowAlert(false);
    }, [isFullCredit]);

    useEffect(() => {
        if (isFullCredit === '' || expenseCreditHistoryLoading || expenseCreditHistoryFailed)
            return setShowLinkedInvoices(false);

        if (linkedInvoices && linkedInvoices.length > 0) return setShowLinkedInvoices(true);

        return setShowLinkedInvoices(false);
    }, [isFullCredit, expenseCreditHistoryLoading, expenseCreditHistoryFailed, linkedInvoices]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="invoice-credit-alert-title"
            maxWidth={false}
            scroll="body"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 2,
                        p: 1.5,
                        width: 536,
                        maxWidth: '90vw',
                        overflow: 'visible',
                    },
                },
            }}
        >
            <DialogTitle id="invoice-credit-alert-title" sx={{ pb: 0 }}>
                <Typography variant="h5" sx={{ mb: 3, mt: 3 }}>
                    You are attempting to edit an approved expense, do you want to continue?
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ overflow: 'visible' }}>
                {expenseCreditHistoryLoading && <CircularProgress color="primary" />}

                {showLinkedInvoices && (
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 3,
                            mt: 2.5,
                            fontWeight: 400,
                            lineHeight: '22px',
                            fontSize: '14px',
                        }}
                    >
                        This Expense is linked to these invoices:{' '}
                        {Array.isArray(linkedInvoices) && linkedInvoices.length
                            ? linkedInvoices.map(({ id, invoiceNumber }, idx) => (
                                  <Box component="span" key={id} sx={{ display: 'inline' }}>
                                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                      <Link
                                          href="#"
                                          onClick={(e) => {
                                              e.preventDefault();
                                              openPdf(id);
                                          }}
                                          underline="hover"
                                          sx={{
                                              color: COLORS.LT_JOURNEY,
                                              whiteSpace: 'nowrap',
                                          }}
                                          target="_blank"
                                          rel="noopener"
                                      >
                                          {invoiceNumber}
                                      </Link>
                                      {idx < linkedInvoices.length - 1 ? (
                                          <span>{' | '}</span>
                                      ) : null}
                                  </Box>
                              ))
                            : '—'}
                    </Typography>
                )}

                {showAlert && (
                    <Alert severity="info" sx={{ mb: 1 }}>
                        A credit memo option has already been selected for the associated invoice.
                        That selection ({isFullCredit ? 'Full Credit' : 'Partial Credit'}) will be
                        applied once this expense is posted.
                    </Alert>
                )}

                {expenseCreditHistoryFailed && (
                    <Typography
                        variant="caption"
                        sx={{ color: COLORS.LT_PEPPER, display: 'block' }}
                        role="alert"
                        aria-live="assertive"
                    >
                        Failed to load expense credit history.{' '}
                        <Typography
                            variant="caption"
                            role="button"
                            onClick={handleRetry}
                            underline="hover"
                            sx={{
                                color: COLORS.LT_JOURNEY,
                                cursor: 'pointer',
                                display: 'inline',
                                underline: 'hover',
                            }}
                        >
                            Retry
                        </Typography>
                    </Typography>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    gap: 1.5,
                    justifyContent: 'flex-start',
                    pl: 3,
                    pb: 3,
                }}
            >
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        textTransform: 'none',
                        borderRadius: '4px',
                        minWidth: 120,
                        borderColor: COLORS.LT_JOURNEY,
                        color: COLORS.LT_JOURNEY,
                        backgroundColor: COLORS.WHITE,
                        '&:hover': {
                            borderColor: COLORS.LT_JOURNEY125,
                            color: COLORS.LT_JOURNEY125,
                            backgroundColor: COLORS.WHITE,
                        },
                        '& .MuiTouchRipple-root': { color: COLORS.LT_JOURNEY },
                        py: 1,
                        px: 6,
                    }}
                >
                    Cancel
                </Button>

                <Button
                    onClick={onConfirm}
                    variant="contained"
                    loading={isLoading}
                    disabled={expenseCreditHistoryFailed}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '4px',
                        minWidth: 120,
                        backgroundColor: COLORS.LT_JOURNEY,
                        color: COLORS.WHITE,
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: COLORS.LT_JOURNEY125,
                            boxShadow: 'none',
                        },
                        py: 1,
                        px: 6,
                    }}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExpenseCorrectionDialog;
